import {
  DEFAULT_VOICE,
  presentVoiceText,
  renderVoiceCopy,
  voiceSchema,
  type Voice,
  type VoiceCopyParams,
} from '@gb-ticket/shared-types';
import { createContext, useContext, type ReactNode } from 'react';

import { useMe } from '@/platform/auth/use-me';
import { usePublicBrand } from '@/platform/brand/use-public-brand';

const VoiceContext = createContext<Voice>(DEFAULT_VOICE);

export function voiceFrom(source: Partial<Voice> | null | undefined): Voice {
  const parsed = voiceSchema.safeParse({
    addressForm: source?.addressForm ?? DEFAULT_VOICE.addressForm,
    personNameParts: source?.personNameParts ?? DEFAULT_VOICE.personNameParts,
    personNameOrder: source?.personNameOrder ?? DEFAULT_VOICE.personNameOrder,
  });
  return parsed.success ? parsed.data : DEFAULT_VOICE;
}

/** Reads `/me`, then the public brand, and publishes that voice to the tree. */
export function VoiceProvider({ children }: { children: ReactNode }) {
  const me = useMe();
  const brand = usePublicBrand();
  const voice = voiceFrom(me.data ?? brand.data);
  return <VoiceContext.Provider value={voice}>{children}</VoiceContext.Provider>;
}

/** Signed-in voice from the provider. Outside it, the shipped default (Sie). */
export function useVoice(): Voice {
  return useContext(VoiceContext);
}

export function useVoiceCopy() {
  const voice = useVoice();
  return (id: string, params?: VoiceCopyParams) => renderVoiceCopy(id, voice, params);
}

export function usePresentVoiceText() {
  const voice = useVoice();
  return (text: string | null | undefined) => (text ? presentVoiceText(text, voice) : '');
}
